<script setup lang="ts">
import Pdf from "@/components/Pdf.vue"
import type { AuditPersonUploadTask } from "@/model/app/audit-person-upload-task"
import type { FrameworkAgreementHolder } from "@/model/app/framework-agreement-holder"
import { GovernedDocumentApprovalState } from "@/model/app/governed-document-approval-state"
import type { PersonInterface } from "@/model/app/person-interface"
import type { TeamType } from "@/model/app/team-type"
import { renderConnectionStatus } from "@/pages/App/OrganizationMember/renderConnectionStatus"
import { defineTranslations } from "@/utils/defineTranslations"
import { $date, sleep, useHttpClient } from "@/vf"
import { computed, reactive, type Ref, ref, toRefs, watchEffect } from "vue"
import { type TasksProps } from "./tasks-props"

const props = defineProps<TasksProps<AuditPersonUploadTask>>()
const emit = defineEmits<{
    (e: "updated"): void
}>()

const { task } = toRefs(props)

const http = useHttpClient()
const updateErrors = ref<string[]>([])

async function save() {
    const errors = []
    if (props.task.updateFrameworkAgreement && newStatus.frameworkAgreement == "ignore") {
        errors.push("frameworkAgreement")
    }
    if (props.task.updatePictureApproval && newStatus.pictureApproval == "ignore") {
        errors.push("picture")
    }
    if (account.value?.gender === "x" && !teamType.value) {
        errors.push("teamType")
    }
    if (errors.length > 0) {
        for (let i = 0; i < 3; i++) {
            updateErrors.value = errors
            await sleep(200)
            updateErrors.value = []
            await sleep(200)
        }
        return
    }

    await http.post(`/task/audit-person-upload/${props.task.id}/save`, {
        documentPageAssignment: task.value.documentPageAssignment,
        ...newStatus,
        teamType: teamType,
    })

    emit("updated")
}

const uploadedFile = computed(() => props.task.attachments?.find(a => a.originalFilename == "uploaded-file.pdf"))

type DocumentAction = "ignore" | "set" | "unset"

function nextStatus(currentStatus: DocumentAction, statusAfterIgnore: DocumentAction = "set") {
    switch (currentStatus) {
        case "set":
            return "unset"
        case "unset":
            return "ignore"
        case "ignore":
            return statusAfterIgnore
    }
}

const newStatus = reactive<{
    pictureApproval: DocumentAction
    frameworkAgreement: DocumentAction
}>({
    pictureApproval: "ignore",
    frameworkAgreement: "ignore",
})
if (task.value.updatePersonData) {
    newStatus.pictureApproval = "unset"
    newStatus.frameworkAgreement = "unset"
}

const account = computed(() => props.task.account as (PersonInterface & FrameworkAgreementHolder) | undefined)

const teamType: Ref<TeamType | null> = ref(null)

watchEffect(() => {
    teamType.value = account.value?.gender === "f" ? "allgirl" : account.value?.gender === "m" ? "coed" : null
})

const trans = defineTranslations({
    rejected_or_cancelled: {
        de: 'Das Dokument wurde abgelehnt oder abgebrochen. Bei Fragen klicke unten auf "Nachricht."',
        en: 'The document was rejected or canceled. If you have questions, click "Message" below.',
    },
    accepted: {
        de: 'Das Dokument wurde akzeptiert. Bei Fragen klicke unten auf "Nachricht."',
        en: 'The document was accepted. If you have questions, click "Message" below.',
    },
})
</script>

<template>

    <div class="p-4 bg-white">
        <div v-if="task.creatorName" class="d-flex justify-content-end align-items-center">
            <i class="fas fa-user mr-2"></i>
            <span class="text-primary">
                {{ task.creatorName }}
            </span>
        </div>
        <div v-if="task.state === 'rejected' || task.state === 'canceled'" class="py-2">
            {{ trans("rejected_or_cancelled") }}
        </div>
        <div v-else-if="task.state === 'closed'" class="py-2">
            {{ trans("accepted") }}
        </div>
        <div class="row" v-else>
            <div class="col-md-6 align-self-stretch">
                <div class="mt-3" v-if="uploadedFile">
                    <pdf :file="`/tasks/task/${task.id}/attachment/${uploadedFile.id}/download`"></pdf>
                </div>
                <div v-else class="h-100 text-center d-flex justify-content-center flex-column">
                    <div>
                        {{ $t("@tasks:tasks.audit_athlete_upload.no_file") }}
                    </div>
                </div>

            </div>
            <div class="col-md-6">
                <div v-if="viewMode === 'admin' && uploadedFile">
                    <b>{{ $t("@tasks:tasks.audit_athlete_upload.page_assignment") }}</b>
                    <table class="table">
                        <thead>
                            <tr>
                                <th>{{ $t("@tasks:tasks.audit_athlete_upload.page_assignment_table.pdfPage") }}</th>
                                <th>{{ $t("@tasks:tasks.audit_athlete_upload.page_assignment_table.qrName") }}</th>
                                <th>{{ $t("@tasks:tasks.audit_athlete_upload.page_assignment_table.qrVersion") }}</th>
                                <th>{{ $t("@tasks:tasks.audit_athlete_upload.page_assignment_table.qrPage") }}</th>
                                <th>{{ $t("@tasks:tasks.audit_athlete_upload.page_assignment_table.assignment") }}</th>
                            </tr>
                        </thead>
                        <tr v-for="(page, index) of task.documentPageAssignment" :key="index">
                            <td>{{ page.pdfPage }}</td>
                            <td>{{ page.conditionName }}</td>
                            <td>{{ page.conditionVersion }}</td>
                            <td>{{ page.uuidPage }}</td>
                            <td>
                                <select class="form-control" v-model="page.assignment">
                                    <option :value="undefined">Ignorieren</option>
                                    <option value="other">Sonstiges</option>
                                    <option value="General.Athletes">Rahmenvereinbarung</option>
                                    <option value="Athlete.PictureApproval">Fotofreigabe</option>
                                </select>
                                <br />
                                <input
                                    class="form-control"
                                    v-model="page.description"
                                    v-if="page.assignment === 'other'"
                                    placeholder="Beschreibung"
                                />
                            </td>
                        </tr>
                    </table>
                </div>
                <div>
                    <b>{{ $t("@tasks:tasks.audit_athlete_upload.member_data") }}</b>
                    <table class="table">
                        <thead>
                            <tr v-if="task.updatePersonData">
                                <td></td>
                                <td>
                                    <b>{{ $t("@tasks:tasks.audit_athlete_upload.personData.old") }}</b>
                                </td>
                                <td></td>
                                <td>
                                    <b>{{ $t("@tasks:tasks.audit_athlete_upload.personData.new") }}</b>
                                </td>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>{{ $t("@tasks:tasks.audit_athlete_upload.firstName") }}</td>
                                <td>{{ account?.firstName }}</td>
                                <td></td>
                                <td
                                    v-if="task.updatePersonData"
                                    :class="{
                                        'text-danger': account?.firstName != task.changedPersonData.firstName,
                                    }"
                                >
                                    {{ task.changedPersonData.firstName }}
                                </td>
                            </tr>
                            <tr>
                                <td>{{ $t("@tasks:tasks.audit_athlete_upload.lastName") }}</td>
                                <td>{{ account?.lastName }}</td>
                                <td></td>
                                <td
                                    v-if="task.updatePersonData"
                                    :class="{
                                        'text-danger': account?.lastName != task.changedPersonData.lastName,
                                    }"
                                >
                                    {{ task.changedPersonData.lastName }}
                                </td>
                            </tr>
                            <tr>
                                <td>{{ $t("@tasks:tasks.audit_athlete_upload.gender") }}</td>
                                <td>
                                    {{ $t("crud:gender." + account?.gender) }}
                                </td>
                                <td :class="{ 'bg-danger': updateErrors.includes('teamType') }">
                                    <div class="form-check form-check-inline">
                                        <input
                                            class="form-check-input"
                                            type="radio"
                                            name="teamType"
                                            id="teamTypeAllgirl"
                                            value="allgirl"
                                            v-model="teamType"
                                            :disabled="account?.gender !== 'x'"
                                        />
                                        <label class="form-check-label" for="teamTypeAllgirl">
                                            {{ $t("@app:organization_member.team_type.allgirl") }}
                                        </label>
                                    </div>
                                    <div class="form-check form-check-inline">
                                        <input
                                            class="form-check-input"
                                            type="radio"
                                            name="teamType"
                                            id="teamTypeCoed"
                                            value="coed"
                                            v-model="teamType"
                                            :disabled="account?.gender !== 'x'"
                                        />
                                        <label class="form-check-label" for="teamTypeCoed">
                                            {{ $t("@app:organization_member.team_type.coed") }}
                                        </label>
                                    </div>
                                </td>
                                <td
                                    v-if="task.updatePersonData"
                                    :class="{
                                        'text-danger': account?.gender != task.changedPersonData.gender,
                                    }"
                                >
                                    {{ $t("crud:gender." + task.changedPersonData.gender) }}
                                </td>
                            </tr>
                            <tr>
                                <td>{{ $t("@tasks:tasks.audit_athlete_upload.birthDate") }}</td>
                                <td>{{ $date(account?.birthDate) }}</td>
                                <td></td>
                                <td
                                    v-if="task.updatePersonData"
                                    :class="{
                                        'text-danger':
                                            $date(account?.birthDate) != $date(task.changedPersonData.birthDate),
                                    }"
                                >
                                    {{ $date(task.changedPersonData.birthDate) }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <div class="my-3">
                    <b>{{ $t("@tasks:tasks.audit_athlete_upload.connections") }}</b>
                    <br />
                    <component :is="() => (account ? renderConnectionStatus(account, $t) : '')"></component>
                </div>
                <b>{{ $t("@tasks:tasks.audit_athlete_upload.comment") }}</b>
                <div class="bg-light border-primary border-bottom p-3">
                    {{ task.comment || "-" }}
                </div>
                <div v-if="viewMode === 'admin'" class="mt-3">
                    <b>{{ $t("@tasks:tasks.audit_athlete_upload.actions") }}</b>
                    <table class="my-3">
                        <thead>
                            <tr>
                                <th></th>
                                <th>{{ $t("@tasks:tasks.audit_athlete_upload.current_status") }}</th>
                                <th>{{ $t("@tasks:tasks.audit_athlete_upload.requested_status") }}</th>
                                <th v-if="task.state === 'open'">
                                    {{ $t("@tasks:tasks.audit_athlete_upload.set_status") }}
                                </th>
                                <th v-else>{{ $t("@tasks:tasks.audit_athlete_upload.new_status") }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr class="text-center">
                                <td class="text-left">{{ $t("@tasks:tasks.audit_athlete_upload.privacy") }}</td>
                                <td>
                                    <i
                                        :class="{
                                            'doc-icon-status-active':
                                                account?.frameworkAgreement?.approvalState ===
                                                GovernedDocumentApprovalState.Approved,
                                            'doc-icon-status-inactive':
                                                account?.frameworkAgreement?.approvalState !==
                                                GovernedDocumentApprovalState.Approved,
                                        }"
                                    ></i>
                                </td>
                                <td>
                                    <i
                                        :class="{
                                            'doc-icon-status-active': task.updateFrameworkAgreement
                                                ? task.updateFrameworkAgreement == 'update'
                                                : account?.frameworkAgreement?.approvalState ===
                                                  GovernedDocumentApprovalState.Approved,
                                            'doc-icon-status-inactive': task.updateFrameworkAgreement
                                                ? task.updateFrameworkAgreement == 'revoke'
                                                : account?.frameworkAgreement?.approvalState !==
                                                  GovernedDocumentApprovalState.Approved,
                                        }"
                                    ></i>
                                </td>
                                <td v-if="task.state === 'open'">
                                    <div
                                        class="border border-light d-inline-block px-1"
                                        :class="{ 'bg-danger': updateErrors.includes('frameworkAgreement') }"
                                        @click="newStatus.frameworkAgreement = nextStatus(newStatus.frameworkAgreement)"
                                    >
                                        <i
                                            :class="{
                                                'doc-icon-status-active': newStatus.frameworkAgreement == 'set',
                                                'doc-icon-status-inactive': newStatus.frameworkAgreement == 'unset',
                                                'doc-icon-status-none': newStatus.frameworkAgreement == 'ignore',
                                            }"
                                        ></i>
                                    </div>
                                </td>
                                <td v-else>
                                    <i
                                        :class="{
                                            'doc-icon-status-active': task.updateFrameworkAgreementPerformed == 'set',
                                            'doc-icon-status-inactive':
                                                task.updateFrameworkAgreementPerformed == 'unset',
                                            'doc-icon-status-none': task.updateFrameworkAgreementPerformed == 'ignore',
                                        }"
                                    ></i>
                                </td>
                            </tr>
                            <tr class="text-center">
                                <td class="text-left">{{ $t("@tasks:tasks.audit_athlete_upload.picture") }}</td>
                                <td>
                                    <i
                                        :class="{
                                            'doc-icon-status-active':
                                                account?.pictureApproval?.approvalState ===
                                                GovernedDocumentApprovalState.Approved,
                                            'doc-icon-status-inactive':
                                                account?.pictureApproval?.approvalState !==
                                                GovernedDocumentApprovalState.Approved,
                                        }"
                                    ></i>
                                </td>
                                <td>
                                    <i
                                        :class="{
                                            'doc-icon-status-active': task.updatePictureApproval
                                                ? task.updatePictureApproval == 'update'
                                                : account?.pictureApproval?.approvalState ===
                                                  GovernedDocumentApprovalState.Approved,
                                            'doc-icon-status-inactive': task.updatePictureApproval
                                                ? task.updatePictureApproval == 'revoke'
                                                : account?.pictureApproval?.approvalState !==
                                                  GovernedDocumentApprovalState.Approved,
                                        }"
                                    ></i>
                                </td>
                                <td v-if="task.state === 'open'">
                                    <div
                                        class="border border-light d-inline-block px-1"
                                        :class="{ 'bg-danger': updateErrors.includes('picture') }"
                                        @click="newStatus.pictureApproval = nextStatus(newStatus.pictureApproval)"
                                    >
                                        <i
                                            :class="{
                                                'doc-icon-status-active': newStatus.pictureApproval == 'set',
                                                'doc-icon-status-inactive': newStatus.pictureApproval == 'unset',
                                                'doc-icon-status-none': newStatus.pictureApproval == 'ignore',
                                            }"
                                        ></i>
                                    </div>
                                </td>
                                <td v-else>
                                    <i
                                        :class="{
                                            'doc-icon-status-active': task.updatePictureApprovalPerformed == 'set',
                                            'doc-icon-status-inactive': task.updatePictureApprovalPerformed == 'unset',
                                            'doc-icon-status-none': task.updatePictureApprovalPerformed == 'ignore',
                                        }"
                                    ></i>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    <div class="text-right" v-if="task.state === 'open'">
                        <button class="btn btn-primary" @click="save()">
                            {{ $t("@tasks:tasks.audit_athlete_upload.save") }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
